/**
 * Grand Quack Auto — screen-space outline post-process pass.
 *
 * Detects edges via luma discontinuities in the rendered image (Sobel filter).
 * Draws dark outlines on depth/color edges for a cel-shading look.
 * Cheap: single fullscreen pass, no extra render targets or scene re-renders.
 */

import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

const OUTLINE_SHADER = {
  uniforms: {
    tDiffuse: { value: null },
    uResolution: { value: [1920, 1080] },
    uThickness: { value: 1.0 },
    uStrength: { value: 0.6 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 uResolution;
    uniform float uThickness;
    uniform float uStrength;
    varying vec2 vUv;

    float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

    void main() {
      vec2 texel = uThickness / uResolution;
      // Sobel kernel on luma — detects color edges.
      float tl = luma(texture2D(tDiffuse, vUv + vec2(-texel.x, texel.y)).rgb);
      float t  = luma(texture2D(tDiffuse, vUv + vec2(0.0, texel.y)).rgb);
      float tr = luma(texture2D(tDiffuse, vUv + vec2(texel.x, texel.y)).rgb);
      float l  = luma(texture2D(tDiffuse, vUv + vec2(-texel.x, 0.0)).rgb);
      float r  = luma(texture2D(tDiffuse, vUv + vec2(texel.x, 0.0)).rgb);
      float bl = luma(texture2D(tDiffuse, vUv + vec2(-texel.x, -texel.y)).rgb);
      float b  = luma(texture2D(tDiffuse, vUv + vec2(0.0, -texel.y)).rgb);
      float br = luma(texture2D(tDiffuse, vUv + vec2(texel.x, -texel.y)).rgb);

      float gx = -tl - 2.0*l - bl + tr + 2.0*r + br;
      float gy = -tl - 2.0*t - tr + bl + 2.0*b + br;
      float edge = length(vec2(gx, gy));

      vec3 color = texture2D(tDiffuse, vUv).rgb;
      // Dark outline where edges are strong.
      color = mix(color, vec3(0.05, 0.02, 0.0), smoothstep(0.1, 0.3, edge) * uStrength);
      gl_FragColor = vec4(color, 1.0);
    }
  `,
};

/** Create the outline pass. Insert into composer AFTER RenderPass, BEFORE bloom. */
export function createOutlinePass(): ShaderPass {
  const pass = new ShaderPass(OUTLINE_SHADER);
  return pass;
}

/** Update resolution uniform (call on window resize). */
export function updateOutlineResolution(pass: ShaderPass, w: number, h: number): void {
  if (pass.uniforms.uResolution) pass.uniforms.uResolution.value = [w, h];
}
